import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Wrench, Cog, MapPin, ClipboardList, FileText, CalendarClock, Gauge, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PMDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pm/dashboard`, { withCredentials: true });
      setStats(res.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cards = [
    { label: 'Equipment', value: stats?.total_equipment ?? '—', color: 'text-gray-900' },
    { label: 'Functional Locations', value: stats?.total_functional_locations ?? '—', color: 'text-gray-900' },
    { label: 'Open Notifications', value: stats?.open_notifications ?? '—', color: 'text-amber-700' },
    { label: 'Breakdowns', value: stats?.breakdown_notifications ?? '—', color: 'text-red-600' },
    { label: 'Open Work Orders', value: stats?.open_orders ?? '—', color: 'text-sky-700' },
    { label: 'Upcoming PM Calls', value: stats?.upcoming_calls ?? '—', color: 'text-green-700' },
    { label: 'Planned Cost', value: stats ? `$${stats.total_planned_cost}` : '—', color: 'text-gray-900' },
    { label: 'Actual Cost', value: stats ? `$${stats.total_actual_cost}` : '—', color: 'text-orange-700' },
  ];

  const links = [
    { path: '/maintenance/equipment', icon: Cog, label: 'Equipment', desc: 'Machines & technical assets' },
    { path: '/maintenance/functional-locations', icon: MapPin, label: 'Functional Locations', desc: 'Plant structure hierarchy' },
    { path: '/maintenance/notifications', icon: ClipboardList, label: 'Notifications', desc: 'Malfunctions & requests' },
    { path: '/maintenance/orders', icon: FileText, label: 'Work Orders', desc: 'Plan, confirm & settle repairs' },
    { path: '/maintenance/task-lists', icon: Wrench, label: 'Task Lists', desc: 'Standard maintenance operations' },
    { path: '/maintenance/maintenance-plans', icon: CalendarClock, label: 'Maintenance Plans', desc: 'Preventive scheduling' },
    { path: '/maintenance/measuring-points', icon: Gauge, label: 'Measuring Points', desc: 'Counters & condition readings' },
  ];

  const byStatus = stats?.orders_by_status || {};

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="pm-dashboard-page">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><Wrench className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Plant Maintenance</h1>
            <p className="text-gray-600 text-sm mt-0.5">Technical objects, notifications, work orders and preventive maintenance.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map((c) => (
            <Card key={c.label} className="p-4">
              <div className="text-xs text-gray-500">{c.label}</div>
              <div className={`text-2xl font-bold mt-1 ${c.color}`} data-testid={`pm-kpi-${c.label}`}>{c.value}</div>
            </Card>
          ))}
        </div>

        {Object.keys(byStatus).length > 0 && (
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-900">
              <AlertTriangle className="h-4 w-4 text-sky-600" />Work Orders by Status
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(byStatus).map(([s, n]) => (
                <div key={s} className="px-3 py-2 rounded-lg bg-gray-50 border" data-testid={`pm-orderstatus-${s}`}>
                  <div className="text-xs text-gray-500">{s}</div>
                  <div className="text-lg font-bold text-gray-900">{n}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {links.map((l) => (
            <Card key={l.path} className="p-5 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(l.path)} data-testid={`pm-link-${l.label}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-50 text-sky-700"><l.icon className="h-5 w-5" /></div>
                <div>
                  <div className="font-semibold text-gray-900">{l.label}</div>
                  <div className="text-sm text-gray-500">{l.desc}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default PMDashboard;
